import Vue, { computed } from 'vue';
import logoLogoutLayoutRoutes from '@/ui/routes/logoLogoutLayoutRoutes';
import { Route } from 'vue-router';
import { VueRouter } from './VueRouter';
import NavbarLayout from '@/ui/layouts/NavbarLayout';
import EmptyLayout from '@/ui/layouts/EmptyLayout';
import NoNavbarLayout from '@/ui/layouts/NoNavbarLayout';
import { LogoutPage } from '@/ui/pages/auth/LogoutPage';
import { guardAuthenticatedRoutes, guardGuestRoutes } from './guards';
import { authenticatedRoutes } from './authenticatedRoutes';
import { guestRoutes } from './guestRoutes';
import { noNavbarRoutes } from './noNavbarRoutes';
import { NamedRouteConfig, RouteName } from './types';
import { NotFoundPage } from '../pages/error/NotFoundPage';
import TestLayout from '@/ui/layouts/TestLayout';
import { testRoutes } from '@/ui/routes/testRoutes';
import { ForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import LogoLayout from '@/ui/layouts/LogoLayout';
import logoLayoutRoutes from '@/ui/routes/logoLayoutRoutes';
import { guestLogoLayoutRoutes } from '@/ui/routes/guestLogoLayoutRoutes';

/**
 * Note: we do not use async page load in order to prevent Webpack
 * generating a chunk per page. This decision was made specifically to have a robust
 * webapp assets load.
 * In case of async routes usage, performance improvement in chrome is only 20%
 * and not so significant to take additional risks of errors.
 * See these documents:
 * https://zypone.atlassian.net/browse/SL-2194
 * https://docs.google.com/document/d/1bNzAsMl-tWFV9PV61WZcapIThwDX_ayYcQ2j_fOLjuQ/edit
 * https://docs.google.com/document/d/1CdgEjxk_7ccP8_YnGpf3Bf2dBxVzRtGD4XfwkD5Mq7M/edit
 */
const routes: NamedRouteConfig[] = [
  {
    path: '/',
    name: 'home',
    redirect: () => ({ name: 'homeNew' }),
  },
  {
    path: '/', // this is needed but is not used
    component: EmptyLayout,
    children: guestRoutes,
    beforeEnter: guardGuestRoutes,
  },
  {
    path: '/', // this is needed but is not used
    component: LogoLayout,
    children: guestLogoLayoutRoutes,
  },
  {
    path: '/', // this is needed but is not used
    component: TestLayout,
    children: testRoutes,
    beforeEnter: guardAuthenticatedRoutes,
  },
  {
    path: '/', // this is needed but is not used
    component: NoNavbarLayout,
    children: noNavbarRoutes,
    beforeEnter: guardAuthenticatedRoutes,
  },
  {
    path: '/', // this is needed but is not used
    component: NavbarLayout,
    children: authenticatedRoutes,
    beforeEnter: guardAuthenticatedRoutes,
  },
  {
    path: '/',
    component: LogoLayout,
    children: logoLayoutRoutes,
  },
  {
    path: '/',
    component: LogoLayout,
    props: {
      showLogout: true,
    },
    children: logoLogoutLayoutRoutes,
  },
  {
    path: '/', // this is needed but is not used
    component: EmptyLayout,
    children: [
      {
        path: '/logout',
        name: 'logout',
        component: LogoutPage,
      },
    ],
    beforeEnter: guardAuthenticatedRoutes,
  },
  {
    name: 'forbidden',
    path: '/forbidden',
    component: ForbiddenPage,
  },
  {
    name: 'notFound',
    path: '*',
    component: NotFoundPage,
  },
  {
    path: '*',
    component: NotFoundPage,
  },
];

export const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

// this part converts each route page title by a specific pattern
const observableRoute: { route: Route | Record<string, unknown> } = {
  route: {},
};
const title = 'Lumiform';
const routeData = Vue.observable(observableRoute);
router.afterEach(route => {
  routeData.route = route;
  document.title = route.meta && route.meta.title ? `${route.meta.title} | ${title}` : title;
});

export function useCurrentRoute() {
  return computed(() => routeData.route as Route & { name: RouteName });
}

export { RouteName } from './types';
